import { AiFigure } from "../../types/Ai";

const isList = (value: unknown): value is unknown[] => Array.isArray(value);
const isNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

/**
 * A figure as the screen can draw it, or null when it can't. Ranges saved
 * before 2026-11-02 came with their numbers flat ({ type: "range", min, q1, … })
 * instead of under `range`; both are read. Anything incomplete is skipped,
 * so one bad figure never takes the conversation down.
 */
export const askFigure = (figure: unknown): AiFigure | null => {
    const f = figure as any;
    if (!f || typeof f !== "object" || typeof f.type !== "string") return null;
    switch (f.type) {
        case "table":
            return isList(f.columns) && isList(f.rows) ? f : null;
        case "bars":
        case "columns":
            return isList(f.items) ? f : null;
        case "flow":
            return isList(f.nodes) ? f : null;
        case "timeline":
            return isList(f.items) ? f : null;
        case "org":
            return f.root && typeof f.root === "object" ? f : null;
        case "range": {
            const r = f.range && typeof f.range === "object" ? f.range : f;
            if (![r.min, r.q1, r.median, r.q3, r.max].every(isNumber)) return null;
            const value = isNumber(r.value) ? r.value : r.median;
            return {
                type: "range",
                title: String(f.title ?? r.column ?? "Range"),
                range: {
                    column: String(r.column ?? ""), label: String(r.label ?? "Median"), value,
                    values: isList(r.values) ? r.values.filter(isNumber) : [], skipped: isNumber(r.skipped) ? r.skipped : 0,
                    min: r.min, q1: r.q1, median: r.median, q3: r.q3, max: r.max,
                    position: r.position === "below" || r.position === "above" ? r.position : "inside",
                },
            } as AiFigure;
        }
        default:
            return null;
    }
};
